import { useState, type Ref } from 'react';
import type {
  CarrierOption,
  LocationMatch,
  TrackingSearchResponse,
  UserSearchHit,
} from '@citibox-panel/api-client';
import { PhoneSearch } from '../../phone-search/components/PhoneSearch';
import { AddressSearch } from '../../location-search';
import { TrackingSearch } from '../../tracking-search/components/TrackingSearch';
import { SearchTypeSelect, type SearchType } from './SearchTypeSelect';

export interface HeaderSearchProps {
  searchUsers: (phone: string) => Promise<UserSearchHit[]>;
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>;
  fetchDeliveryStates: () => Promise<string[]>;
  searchTracking: (tracking: string) => Promise<TrackingSearchResponse>;
  // Where a mode's announcement goes. REQUIRED and the HOST's, not this slice's: a live region has
  // to pre-exist its own text to be heard, and everything under here is mounted and unmounted as
  // the agent changes type. This slice only carries the callback down to the mode that has
  // something to say — it never words a sentence of its own. See the README invariant.
  onAnnounce: (message: string) => void;
  // The shell focuses the header input on ⌘K / «+» / the empty-state action (shell seam).
  inputRef?: Ref<HTMLInputElement>;
}

export function HeaderSearch({
  searchUsers,
  searchLocations,
  fetchLocationCarriers,
  fetchDeliveryStates,
  searchTracking,
  onAnnounce,
  inputRef,
}: HeaderSearchProps) {
  const [type, setType] = useState<SearchType>('phone');

  // exactOptionalPropertyTypes rejects an explicit undefined, so the ref is spread rather than
  // passed. Both modes name it `inputRef`, so one spread serves either.
  const refProp = inputRef !== undefined ? { inputRef } : {};

  // The trigger sits in the same place in every mode — inside the field's end adornment — or
  // «Teléfono» is a mode nothing can leave. The host renders it once and hands it down, so no mode
  // can be built without its way out.
  const typeSelect = <SearchTypeSelect value={type} onChange={setType} />;

  return (
    <>
      {type === 'phone' && (
        <PhoneSearch search={searchUsers} {...refProp} trailing={typeSelect} />
      )}
      {type === 'address' && (
        <AddressSearch
          searchLocations={searchLocations}
          fetchLocationCarriers={fetchLocationCarriers}
          fetchDeliveryStates={fetchDeliveryStates}
          onAnnounce={onAnnounce}
          {...refProp}
          trailing={typeSelect}
        />
      )}
      {type === 'tracking' && (
        <TrackingSearch search={searchTracking} {...refProp} trailing={typeSelect} />
      )}
    </>
  );
}
